import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import ExploreDesktop, { Character } from './ExploreDesktop';
import ExploreMobile from './ExploreMobile';
import { ScenarioDefinitionListResponse } from '../../api/types/scenario';
import styles from './Explore.module.css';

// For development only
const USE_TEST_DATA = false;
const TEST_CHARACTERS: Character[] = [
  {
    id: "1",
    username: "@RandomChairLover",
    display_name: "Step Brothers",
    tagline: "~Your two new older brothers~",
    icon: "/media/characters/mario.png"
  },
  {
    id: "2",
    username: "@l4riaaa",
    display_name: "Alcoholic roomate",
    tagline: "Jay is your roomate, he's 21 and drinks.",
    icon: "/media/characters/luigi.png"
  },
  {
    id: "3",
    username: "@Sarahthe_nova",
    display_name: "Jojo siwa",
    tagline: "She was a bad girl....",
    icon: "/media/characters/mario.png"
  },
  {
    id: "4",
    username: "@GabiSbusy",
    display_name: "Best friend",
    tagline: "Your boy Best friend who has a secret crush on you",
    icon: "/media/characters/luigi.png"
  },
  {
    id: "5",
    username: "@slepiiii",
    display_name: "Sharing a bed",
    tagline: "✿sharing a bed with your enemy✿",
    icon: "/media/characters/mario.png"
  },
  {
    id: "6",
    username: "@ADRIANA_KINDS",
    display_name: "The hot nerd",
    tagline: "The schools nerd",
    icon: "/media/characters/luigi.png"
  }
];

const TEST_SCENARIOS: ScenarioDefinitionListResponse[] = [
  {
    id: "1",
    display_name: "Fantasy Adventure",
    tagline: "Embark on an epic journey through magical lands",
    icon: "/media/scenarios/fantasy.png"
  },
  {
    id: "2",
    display_name: "Space Exploration",
    tagline: "Discover new worlds and alien civilizations",
    icon: "/media/scenarios/space.png"
  },
  {
    id: "3",
    display_name: "Mystery Mansion",
    tagline: "Solve the mystery of the haunted mansion",
    icon: "/media/scenarios/mystery.png"
  }
];

// Define the character filter type
type CharacterFilter = 'trending' | 'featured' | 'top' | 'new';

// Define the content type
type ContentType = 'characters' | 'scenarios';

// Define constants for configuration
// This is the single source of truth for the maximum number of featured items
// Used by both mobile and desktop views
export const MAX_FEATURED_ITEMS = 20; // Maximum number of featured items to display (for two-row layout)

/**
 * Container component that handles all business logic for the Explore feature
 * and renders the appropriate view based on screen size
 */
const ExploreContainer: React.FC = () => {
  // Hooks
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  
  // State
  const [characters, setCharacters] = useState<Character[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioDefinitionListResponse[]>([]);
  const [featuredCharacters, setFeaturedCharacters] = useState<Character[]>([]);
  const [featuredScenarios, setFeaturedScenarios] = useState<ScenarioDefinitionListResponse[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeSearchQuery, setActiveSearchQuery] = useState<string>('');
  const [activeCategory, setActiveCategory] = useState<string>('Trending');
  const [contentType, setContentType] = useState<ContentType>('characters');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isGridLoading, setIsGridLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  
  // Search dropdown state
  const [dropdownCharacters, setDropdownCharacters] = useState<Character[]>([]);
  const [dropdownScenarios, setDropdownScenarios] = useState<ScenarioDefinitionListResponse[]>([]);
  
  // Ref for debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Access parent's sidebar state - We'll get this from the parent component
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  
  // Update sidebar state from parent component's toggle event
  useEffect(() => {
    // Listen for sidebar toggle events from ResponsiveLayout
    const handleSidebarToggle = (e: CustomEvent) => {
      setSidebarExpanded(e.detail.expanded);
    };
    
    window.addEventListener('sidebarToggle' as any, handleSidebarToggle);
    
    return () => {
      window.removeEventListener('sidebarToggle' as any, handleSidebarToggle);
    };
  }, []);
  
  // Constants
  const categories: string[] = ['Trending', 'Featured', 'Top', 'New'];
  const contentTypes = [
    { id: 'characters' as const, label: 'Characters' },
    { id: 'scenarios' as const, label: 'Scenarios' }
  ];

  /**
   * Load characters based on category
   * @param {string} category - The category to filter by
   */
  const loadCharacters = useCallback(async (category: string) => {
    if (USE_TEST_DATA) {
      setCharacters(TEST_CHARACTERS);
      setIsLoading(false);
      setIsGridLoading(false);
      return;
    }

    // Only set grid loading to true, not the entire page
    setIsGridLoading(true);
    setError(null);
    
    try {
      const data = await api.characters.filter(category.toLowerCase() as CharacterFilter);
      setCharacters(data);
    } catch (error) {
      console.error('Failed to load characters:', error);
      setError('Failed to load characters. Please try again.');
      setCharacters([]);
    } finally {
      setIsLoading(false);
      setIsGridLoading(false);
    }
  }, []);

  /**
   * Load scenarios
   */
  const loadScenarios = useCallback(async (category: string = 'trending') => {
    if (USE_TEST_DATA) {
      setScenarios(TEST_SCENARIOS);
      // Set featured scenarios with test data too
      setFeaturedScenarios(TEST_SCENARIOS);
      setIsGridLoading(false);
      return;
    }

    setIsGridLoading(true);
    setError(null);

    try {
      let data;
      
      // Use the filter API if a valid category is provided
      if (['trending', 'featured', 'top', 'new'].includes(category.toLowerCase())) {
        data = await api.scenarioDefinitions.filter(category.toLowerCase() as 'trending' | 'featured' | 'top' | 'new');
      } else {
        // Use search instead of list for invalid categories or when no category is specified
        data = await api.scenarioDefinitions.search(category);
      }
      
      // Validate scenarios data to ensure all have required properties
      const validScenarios = data.filter(scenario => 
        scenario && typeof scenario === 'object' && scenario.id && scenario.display_name
      );
      
      setScenarios(validScenarios);
      
      // Always load featured scenarios separately to ensure they're available
      if (category.toLowerCase() !== 'featured') {
        try {
          const featuredData = await api.scenarioDefinitions.filter('featured');
          const validFeatured = featuredData.filter(scenario => 
            scenario && typeof scenario === 'object' && scenario.id && scenario.display_name
          );
          
          if (validFeatured.length > 0) {
            setFeaturedScenarios(validFeatured.slice(0, MAX_FEATURED_ITEMS));
          } else if (validScenarios.length > 0) {
            // Fallback: If no featured scenarios, use some from the current category
            setFeaturedScenarios(validScenarios.slice(0, MAX_FEATURED_ITEMS));
          }
        } catch (error) {
          console.error('Failed to load featured scenarios:', error);
          // Fallback to using some of the already loaded scenarios as featured
          if (validScenarios.length > 0) {
            setFeaturedScenarios(validScenarios.slice(0, MAX_FEATURED_ITEMS));
          }
        }
      } else if (validScenarios.length > 0) {
        // If we're loading featured, use those
        setFeaturedScenarios(validScenarios.slice(0, MAX_FEATURED_ITEMS));
      }
    } catch (error) {
      console.error('Failed to load scenarios:', error);
      setError('Failed to load scenarios. Please try again.');
      setScenarios([]);
      // Don't clear featured scenarios on error to maintain carousel
    } finally {
      setIsGridLoading(false);
    }
  }, []);

  /**
   * Search characters based on query - completely independent of category
   * This is used for the main grid content
   */
  const searchCharacters = useCallback(async (query: string) => {
    if (USE_TEST_DATA) {
      setCharacters(TEST_CHARACTERS);
      setIsGridLoading(false);
      return;
    }

    // If query is empty, don't perform search
    if (!query.trim()) {
      return;
    }

    // Only set grid loading to true, not the entire page
    setIsGridLoading(true);
    setError(null);
    
    try {
      const results = await api.characters.search(query);
      setCharacters(results);
    } catch (error) {
      console.error('Failed to search characters:', error);
      setError('Search failed. Please try again.');
      setCharacters([]);
    } finally {
      setIsGridLoading(false);
    }
  }, []);

  /**
   * Search scenarios based on query - completely independent of category
   * This is used for the main grid content
   */
  const searchScenarios = useCallback(async (query: string) => {
    if (USE_TEST_DATA) {
      setScenarios(TEST_SCENARIOS);
      setIsGridLoading(false);
      return;
    }

    // If query is empty, don't perform search
    if (!query.trim()) {
      return;
    }

    setIsGridLoading(true);
    setError(null);
    
    try {
      // Use the API's search method
      const results = await api.scenarioDefinitions.search(query);
      setScenarios(results);
    } catch (error) {
      console.error('Failed to search scenarios:', error);
      setError('Search failed. Please try again.');
      setScenarios([]);
    } finally {
      setIsGridLoading(false);
    }
  }, []);

  /**
   * Search for dropdown results - completely independent of category and main grid
   * This handles the search dropdown suggestions
   */
  const searchDropdown = useCallback(async (query: string) => {
    if (!query.trim()) {
      setDropdownCharacters([]);
      setDropdownScenarios([]);
      return;
    }

    try {
      // Fetch dropdown results for characters
      const characterResults = await api.characters.search(query);
      setDropdownCharacters(characterResults);

      // Fetch dropdown results for scenarios
      const scenarioResults = await api.scenarioDefinitions.search(query);
      setDropdownScenarios(scenarioResults);
    } catch (error) {
      console.error('Failed to fetch dropdown results:', error);
      // Don't set error state for dropdown failures
    }
  }, []);

  /**
   * Handle search input change - completely decoupled from grid filtering
   * This only updates the search query state and fetches dropdown results (debounced)
   * @param {string} query - The search query
   */
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query); // Update input value immediately

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If query is empty, reset search state immediately and clear dropdown results
    if (!query.trim()) {
      setIsSearchActive(false);
      setDropdownCharacters([]);
      setDropdownScenarios([]);
      // Don't reset the main grid content here
      debounceTimerRef.current = null; // Clear timer ref
      return; // Exit early, no need to debounce an empty query fetch
    }

    // Set a new debounce timer to fetch dropdown results
    debounceTimerRef.current = setTimeout(() => {
      searchDropdown(query);
    }, 300); // 300ms debounce delay

  }, [searchDropdown]); // Dependency is only searchDropdown

  /**
   * Execute search for main grid content
   * This is called when user explicitly triggers search (e.g., presses Enter)
   */
  const executeSearch = useCallback(() => {
    // Don't execute empty searches
    if (!searchQuery.trim()) {
      return;
    }
    
    setActiveSearchQuery(searchQuery);
    setIsSearching(true);
    setIsSearchActive(true);
    
    if (contentType === 'characters') {
      searchCharacters(searchQuery);
    } else {
      searchScenarios(searchQuery);
    }
  }, [searchQuery, contentType, searchCharacters, searchScenarios]);

  /**
   * Handle search form submission
   * @param {React.FormEvent} event - The form submission event
   */
  const handleSearchSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    executeSearch();
  }, [executeSearch]);

  /**
   * Clear search and reset to category view
   */
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setIsSearchActive(false);
    setDropdownCharacters([]);
    setDropdownScenarios([]);
    
    // Reset to category-based content
    if (contentType === 'characters') {
      loadCharacters(activeCategory.toLowerCase());
    } else {
      loadScenarios(activeCategory.toLowerCase());
    }
  }, [activeCategory, contentType, loadCharacters, loadScenarios]);

  /**
   * Handle category selection - now completely independent of search
   * @param {string} category - The selected category
   */
  const handleCategoryChange = useCallback((category: string) => {
    if (category === activeCategory) return; // Don't reload if same category
    setActiveCategory(category);
    
    // Only load category content if search is not active
    if (!isSearchActive) {
      setIsSearching(true);
      
      // Load content based on the current content type and new category
      if (contentType === 'characters') {
        loadCharacters(category.toLowerCase());
      } else {
        loadScenarios(category.toLowerCase());
      }
    }
    // If search is active, don't change the content - search results remain displayed
  }, [activeCategory, contentType, loadCharacters, loadScenarios, isSearchActive]);

  /**
   * Handle content type change (characters vs scenarios)
   * @param {ContentType} type - The content type to display
   */
  const handleContentTypeChange = useCallback((type: ContentType) => {
    if (type === contentType) return; // Don't reload if same type
    setContentType(type);
    
    // If search is active, perform the search with the new content type
    if (isSearchActive && activeSearchQuery) {
      setIsSearching(true);
      if (type === 'characters') {
        searchCharacters(activeSearchQuery);
      } else {
        searchScenarios(activeSearchQuery);
      }
    } else {
      // Otherwise load content based on category
      setIsSearching(true);
      if (type === 'characters') {
        loadCharacters(activeCategory.toLowerCase());
      } else {
        loadScenarios(activeCategory.toLowerCase());
      }
    }
  }, [contentType, activeCategory, loadCharacters, loadScenarios, isSearchActive, activeSearchQuery, searchCharacters, searchScenarios]);

  /**
   * Handle character selection
   * @param {Character} character - The selected character
   */
  const handleCharacterClick = useCallback(async (character: Character) => {
    try {
      // If not logged in, navigate to preview mode
      if (!isLoggedIn) {
        navigate(`/chats-preview/${character.username}`, { 
          state: { previewCharacter: character, sourceUrl: '/explore' } 
        });
        return;
      }
      
      // Otherwise create a real chat
      const chat = await api.chats.create(character.id.toString());
      // Don't pass the chat object in state, it contains non-serializable data
      navigate(`/chats/${chat.id}`, { 
        state: { sourceUrl: '/explore' } 
      });
    } catch (error) {
      console.error('Failed to create chat:', error);
      setError('Failed to start chat. Please try again.');
    }
  }, [isLoggedIn, navigate]);

  /**
   * Handle scenario selection
   * @param {ScenarioDefinitionListResponse} scenario - The selected scenario
   */
  const handleScenarioClick = useCallback((scenario: ScenarioDefinitionListResponse) => {
    navigate(`/scenarios/${scenario.id}`);
  }, [navigate]);

  // Load content on mount
  useEffect(() => {
    const initialLoad = async () => {
      setIsLoading(true);
      
      try {
        // First, load featured scenarios to prevent layout shifts
        try {
          const featuredData = await api.scenarioDefinitions.filter('featured');
          const validFeatured = featuredData.filter(scenario => 
            scenario && typeof scenario === 'object' && scenario.id && scenario.display_name
          );
          
          if (validFeatured.length > 0) {
            setFeaturedScenarios(validFeatured.slice(0, MAX_FEATURED_ITEMS));
          }
        } catch (error) {
          console.error('Failed to load featured scenarios in initialLoad:', error);
          // We'll rely on the fallback in loadScenarios
        }
        
        // Load featured characters using the featured filter instead of trending
        const featuredCharactersData = USE_TEST_DATA 
          ? TEST_CHARACTERS 
          : await api.characters.filter('featured');
        
        // Initialize featured characters
        if (featuredCharactersData.length) {
          const featured = featuredCharactersData.filter(char => 
            char && typeof char === 'object' && char.id && char.display_name
          ).slice(0, MAX_FEATURED_ITEMS);
          
          setFeaturedCharacters(featured);
        }
        
        // Load trending characters for the main grid
        const trendingCharacters = USE_TEST_DATA
          ? TEST_CHARACTERS
          : await api.characters.filter('trending');
        
        setCharacters(trendingCharacters);
        
        // Load trending scenarios
        await loadScenarios('trending');
      } catch (error) {
        console.error('Error during initial load:', error);
        setError('Failed to load content. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    initialLoad();
  }, [loadScenarios]);

  // Handle category changes for characters and scenarios - only if search is not active
  useEffect(() => {
    if (!isSearchActive) {
      if (contentType === 'characters') {
        loadCharacters(activeCategory.toLowerCase());
      } else {
        loadScenarios(activeCategory.toLowerCase());
      }
    }
  }, [activeCategory, contentType, loadCharacters, loadScenarios, isSearchActive]);

  // Reset isSearching when loading completes
  useEffect(() => {
    if (!isGridLoading) {
      // Add a small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setIsSearching(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isGridLoading]);

  // Show a full-page loading state only on initial load
  if (isLoading) {
    return <div className={styles.fullPageLoading}>Loading</div>;
  }

  // Shared props for both desktop and mobile views
  const sharedProps = {
    characters,
    scenarios,
    featuredCharacters,
    featuredScenarios,
    searchQuery,
    activeSearchQuery,
    activeCategory,
    categories,
    contentType,
    contentTypes,
    isLoading: isGridLoading || isSearching, // Pass both grid loading and searching states
    error,
    handleSearch,
    handleSearchSubmit,
    executeSearch,
    clearSearch,
    handleCategoryChange,
    handleContentTypeChange,
    handleCharacterClick,
    handleScenarioClick,
    sidebarExpanded, // Pass the sidebar state to child components
    isSearchActive, // Pass the search active state to child components
    dropdownCharacters, // Pass dropdown results to child components
    dropdownScenarios // Pass dropdown results to child components
  };

  // Render the appropriate view based on screen size
  return (
    <div className={styles.exploreContainer}>
      {isDesktop ? (
        <ExploreDesktop {...sharedProps} />
      ) : (
        <ExploreMobile {...sharedProps} />
      )}
    </div>
  );
};

export default ExploreContainer; 